import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { forwardRef } from 'react';
const BASE_CLASSES = [
    'inline-flex shrink-0 items-center gap-2',
    'min-h-[44px] min-w-[44px] px-4 py-2',
    'rounded-full border',
    'text-[13px] font-semibold',
    'transition-colors duration-150',
    'snap-start',
    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/60 focus-visible:ring-offset-2 focus-visible:ring-offset-transparent',
].join(' ');
const INACTIVE_CLASSES = 'bg-white/[0.08] border-white/[0.14] text-white backdrop-blur-[14px] hover:bg-white/[0.12]';
/**
 * CategoryChip — pill-shaped button with icon + label.
 *
 * Inactive state uses glassmorphism (translucent white bg + 1px white-14
 * border + backdrop blur). Active state fills with `activeColor` and renders
 * icon + label in `activeForeground` for AA contrast.
 *
 * Designed to live inside a parent with `role="tablist"`. Sets
 * `role="tab"` and `aria-selected` so assistive tech can announce the
 * selection. Consumers are responsible for wiring roving `tabIndex`.
 */
export const CategoryChip = forwardRef(({ icon, label, isActive, activeColor, activeForeground, className = '', style, ...rest }, ref) => {
    const activeStyle = isActive
        ? { backgroundColor: activeColor, borderColor: activeColor, color: activeForeground }
        : undefined;
    const classes = [BASE_CLASSES, isActive ? '' : INACTIVE_CLASSES, className]
        .filter(Boolean)
        .join(' ');
    return (_jsxs("button", { ref: ref, type: "button", role: "tab", "aria-selected": isActive, "data-testid": "category-chip", "data-active": isActive ? 'true' : 'false', className: classes, style: { ...activeStyle, ...style }, ...rest, children: [_jsx("span", { className: "inline-flex items-center", "aria-hidden": "true", children: icon }), _jsx("span", { children: label })] }));
});
CategoryChip.displayName = 'CategoryChip';
